import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import {
  ApiCheckUnlock,
  ApiCheckUnlockPay,
  fetcher, isApiV1Errors, isCheckUnlockStatusOk, isCheckUnlockStatusPayOk,
  ResponseWithHeaders, buildSearchParams,
} from '@utils/data-fetching';
import { bjToast } from '@components/common';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';
import { showCvContactModal } from '@root/src/components/features/modals/employer/CvContactModal';
import {
  showUpgradeSubscriptionModal,
} from '@components/features/modals/employer/subscription/UpgradeSubscriptionModal';
import {
  showAccountSuspendedModal,
} from '@components/features/modals/employer/subscription/AccountSuspendedModal';
import { trackEvent } from '@services/tracking';
import {
  showAccountFirstPaymentModal,
} from '@components/features/modals/employer/subscription/AccountFirstPaymentModal';
import { showAddBillingDataModal } from '@components/features/modals/billing-data/AddBillingDataModal';
import { showUpgradeJobModal } from '@components/templates/my-jobs/UpgradeJobDialog';

interface IProps {
  userIdToUnlock?: number;
  jobSlug?: string;
  jobTitle?: string;
  jobFolderId?: number;
  rid?: string;
  isCompany?: boolean;
  userId?: number;
  slug?: string;
  toFetchOperationJob?: boolean;
  locale?: Locale;
  isPassiveRecommendation?: boolean;
  /** Called after the job was upgraded, so the caller can refresh its data. */
  onJobUpgraded?: () => void;
  /**
   * The candidate applied to a freemium job: there is no per-applicant unlock to
   * confirm, so every unlock entry point opens the job upgrade dialog straight
   * away instead of the pricing confirmation. The backend enforces the same rule
   * with a 607; this just stops us quoting a price we cannot honour.
   */
  contactRequiresJobUpgrade?: boolean;
}

interface UnlockOptions {
  type: 'download' | 'message' | 'contact';
  onSuccess?: (response: ApiCheckUnlock) => void;
}
/**
 *
 * @param userIdToUnlock - the slug of the cv
 * @param rid - query param. can be used in both cases. with slug or jobSlug
 * @param locale - the current app locale.
 * @param jobFolderId - the current app locale.
 * @param jobTitle - the current app locale.
 * @param jobSlug - the current app locale.
 * @param slug - the current app locale.
 * @param userId - the current app locale.
 * @param isPassiveRecommendation - used for letting us know if the cv is a passive recommendation
 * @param onJobUpgraded - callback to be called after the job was upgraded, so the caller can refresh its data.
 * @param contactRequiresJobUpgrade - the candidate applied to a freemium job: there is no per-applicant unlock to
 * confirm, so every unlock entry point opens the job upgrade dialog straight
 */
export const useUnblockOperations = ({
  userIdToUnlock, rid, locale, jobFolderId, jobTitle, jobSlug, slug, userId, isPassiveRecommendation, onJobUpgraded,
  contactRequiresJobUpgrade = false,
}: IProps) => {
  // ************************************
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingUnlockOptions, setPendingUnlockOptions] = useState<UnlockOptions | null>(null);
  const { status606Toast } = useStaticToasts();
  const { t } = useTranslation('common');

  // Keep a ref to always-current job context so unblockCv never uses a stale closure
  const jobContextRef = useRef({ jobFolderId, jobTitle, jobSlug });
  useEffect(() => {
    jobContextRef.current = { jobFolderId, jobTitle, jobSlug };
  }, [jobFolderId, jobTitle, jobSlug]);

  // Same reason as jobContextRef: the upgrade dialog outlives this render, so
  // the callback it fires must be read at call time, not captured.
  const onJobUpgradedRef = useRef(onJobUpgraded);
  useEffect(() => {
    onJobUpgradedRef.current = onJobUpgraded;
  }, [onJobUpgraded]);

  /**
   * Opens the job upgrade dialog for the job in context. Returns false when
   * there is no job to upgrade, so callers can fall back to their normal path
   * rather than swallowing the click.
   */
  const openJobUpgrade = useCallback((): boolean => {
    const { jobSlug: contextJobSlug } = jobContextRef.current;

    if (!contextJobSlug) return false;

    showUpgradeJobModal(contextJobSlug, {
      // The client clicked "contact", not "upgrade" — the dialog has to say why
      // it is asking them to buy a package. It names the job from its own
      // request: jobFolderInfo.title is only populated when there is no jobSlug,
      // or by the confirmation modal we now skip, so it is empty here.
      reason: 'contact-applicant',
      onSuccess: () => onJobUpgradedRef.current?.(),
    });

    return true;
  }, []);

  const handleApiError = useCallback((code: number) => {
    switch (code) {
      case 601:
        showUpgradeSubscriptionModal();
        break;
      case 602:
        showAccountFirstPaymentModal();
        break;
      case 603:
        showAccountSuspendedModal();
        break;
      case 701:
      case 403:
      { const employerData = {} as ApiSchema<'CompanyRead'>;
        showAddBillingDataModal(locale as Locale, employerData, () => {});
        break; }
      case 606:
        status606Toast();
        break;
      // Freemium job: the applicant cannot be unlocked one by one. Upgrading the
      // job to a paid bundle is the way through, and it unlocks every applicant
      // at once — so send the client straight to the upgrade dialog.
      case 607:
        // No job in context (shouldn't happen — the backend only raises 607 for
        // an applicant of a specific job), so at least say why it failed.
        if (!openJobUpgrade()) {
          bjToast(t('unlock.error.freemium_upgrade_required'), { className: 'border-l-3 border-error' });
        }
        break;
      default:
        bjToast(`Error code: ${code}`, { className: 'border-l-3 border-error' });
    }
  }, [locale, status606Toast, t, openJobUpgrade]);

  const unblockCv = useCallback(async ({ type, onSuccess }: UnlockOptions) => {
    if (!userIdToUnlock) return;

    // Freemium applicant: there is nothing to buy per candidate. Handled here
    // rather than only at the call sites so every caller — including the cards,
    // which skip the confirmation modal — lands on the upgrade dialog instead of
    // a 607 round-trip.
    if (contactRequiresJobUpgrade && openJobUpgrade()) return;

    try {
      const queryParams: Record<string, string> = {};
      if (isPassiveRecommendation) {
        queryParams.isPassiveRecommendation = '1';
      }
      if (rid) {
        queryParams.rid = rid;
      }

      const res = await fetcher(
        `/v1/resume-operation/${userIdToUnlock}/check-unlock/${type}${buildSearchParams({ ...queryParams })}`,
        locale,
        {
          method: 'POST',
          payload: {
            jobId: jobContextRef.current.jobFolderId,
            title: jobContextRef.current.jobTitle,
            jobSlug: jobContextRef.current.jobSlug,
          },
          stringifyPayload: true,
          withResponseHeaders: true,
        },
      );

      const { status, response } = res as ResponseWithHeaders<ApiCheckUnlock>;

      if (status === 402) {
        const { response: payResponse } = res as ResponseWithHeaders<ApiCheckUnlockPay>;
        if (isCheckUnlockStatusPayOk(payResponse)) {
          showCvContactModal(payResponse.id, locale as Locale);
        }
        return;
      }

      if (isCheckUnlockStatusOk(response)) {
        if (onSuccess) {
          trackEvent({
            eventType: 'action',
            entityType: 'resume',
            entityId: slug,
            action: 'contact',
            viewContext: 'detail',
            ...(userId && { userId: userId.toString() }),
            ...(rid && { listUuid: rid }),
            metadata: [
              { key: 'type', value: type },
            ],
          });
          onSuccess(response);
        }

        return;
      }

      if (isApiV1Errors(response)) {
        handleApiError(response.errors[0].code as number);
      }
    } catch (err) {
      console.error(err);
    }
  }, [userIdToUnlock, rid, locale, handleApiError, slug, userId, isPassiveRecommendation, contactRequiresJobUpgrade, openJobUpgrade]);

  // ----------------------------
  // Request unlock with confirmation modal
  // ----------------------------
  const requestUnlockWithConfirm = useCallback((options: UnlockOptions) => {
    // Straight to the upgrade dialog: confirming a price we cannot charge, only
    // to replace that modal with another one, is two dialogs for one decision.
    if (contactRequiresJobUpgrade && openJobUpgrade()) return;

    const skipConfirm = localStorage.getItem('skipUnlockConfirm') === 'true';
    if (skipConfirm) {
      void unblockCv(options);
      return;
    }

    setPendingUnlockOptions(options);
    setConfirmModalOpen(true);
  }, [unblockCv, contactRequiresJobUpgrade, openJobUpgrade]);

  const confirmUnlock = useCallback(() => {
    if (pendingUnlockOptions) {
      void unblockCv(pendingUnlockOptions);
      setConfirmModalOpen(false);
      setPendingUnlockOptions(null);
    }
  }, [pendingUnlockOptions, unblockCv]);

  const cancelUnlock = useCallback(() => {
    setConfirmModalOpen(false);
    setPendingUnlockOptions(null);
  }, []);

  const hasSkipUnlockConfirm = useCallback(() => localStorage.getItem('skipUnlockConfirm') === 'true', []);

  const requestUnlock = useCallback((options: UnlockOptions) => {
    void unblockCv(options);
  }, [unblockCv]);


  const openPricingInformationModal = useCallback(() => {
    // No price to inform about on a freemium job.
    if (contactRequiresJobUpgrade) return;

    const skipConfirm = localStorage.getItem('skipUnlockConfirm') === 'true';
    if (skipConfirm) {
      return;
    }
    setConfirmModalOpen(true);
  }, [contactRequiresJobUpgrade]);


  return {
    unblockCv,
    hasSkipUnlockConfirm,
    openPricingInformationModal,
    requestUnlock,
    requestUnlockWithConfirm,
    confirmModalOpen,
    confirmUnlock,
    cancelUnlock,
  };
};

